import React, { useState, useEffect, useRef } from "react";
import { MapPin, Video } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import api from "../../lib/apiClient";
import {
  DRAFT_EXPIRY_MS, MAX_RECORD_SECONDS,
  selectDurationBracket, loadDraft, clearDraft,
} from "../../lib/drafts";
// saveDraft is consumed inside useMediaRecorder (the hook persists the
// draft to IndexedDB on stop).
import { RecorderCommissionBanner } from "./recorder/RecorderCommissionBanner";
import { RecorderCostSummary } from "./recorder/RecorderCostSummary";
import { RecorderPublishFormFields } from "./recorder/RecorderPublishFormFields";
import { RecorderCameraOverlays } from "./recorder/RecorderCameraOverlays";
import { RecorderControlsBar } from "./recorder/RecorderControlsBar";
import { useMediaRecorder } from "./recorder/useMediaRecorder";
import { useCameraStream } from "./recorder/useCameraStream";
import { useRecorderLocation } from "./recorder/useRecorderLocation";
import { useRecorderPublish } from "./recorder/useRecorderPublish";
import {
  DURATION_COSTS, STORAGE_COSTS, BASE_COST, FEATURED_COST,
  DURATION_OPTIONS, STORAGE_OPTIONS,
} from "./recorder/recorderConstants";

/**
 * VideoRecorderDialog — the full Create-Video flow extracted from DashboardPage.
 * Owns: camera lifecycle (via useCameraStream), MediaRecorder
 * (via useMediaRecorder), draft load/resume, form state, and the
 * Publish button. Location is owned by useRecorderLocation, publish
 * logic by useRecorderPublish.
 * Parent owns: open/close + commission attribution (driven by URL params).
 */
export const VideoRecorderDialog = ({
  open,
  onOpenChange,
  user,
  token,
  refreshUser,
  activeCommission,
  commissionLockUntil,
  onVideoCreated,
  onCommissionDone,
}) => {
  // Form state
  const [videoTitle, setVideoTitle] = useState("");
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoStorage, setVideoStorage] = useState(10);
  const [videoFeatured, setVideoFeatured] = useState(false);

  // Location state — owned by useRecorderLocation (auto-fetches when
  // the dialog opens; gracefully falls back to DEFAULT_LOCATION on any
  // permission/timeout/geocode failure).
  const {
    location, setLocation,
    locationError, setLocationError,
    locationLoading,
    getLocation, useDefaultLocation,
  } = useRecorderLocation({ open });

  // Recorder state — the MediaRecorder lifecycle (recordPhase, isRecording,
  // isPaused, recordedBlob/Url, recordTimer, recordStartedAt + start/pause/
  // resume/stop/retake handlers) lives in useMediaRecorder. The camera
  // lifecycle lives in useCameraStream. The form state stays here in
  // the parent because it's coupled to the dialog's commission pre-fill
  // and the publish flow.
  const [draftBannerDismissed, setDraftBannerDismissed] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  // Refs the hooks share. `mediaStreamRef` is owned by useCameraStream but
  // exposed back to us so it can be threaded into useMediaRecorder.
  // `stopRecordingRef` breaks the init-order cycle: useCameraStream needs
  // to call stopRecording() on dialog close (recorder owns the camera-track
  // teardown via its `onstop` handler), but useMediaRecorder is initialised
  // *after* useCameraStream. Refs are mutable so the timing works.
  // `isRecordingRef` / `recordPhaseRef` do the same job for the recorder
  // state that the camera hook reads inside its event handlers.
  const liveVideoRef = useRef(null);
  const reviewVideoRef = useRef(null);
  const locationRef = useRef(null);
  const stopRecordingRef = useRef(null);
  const isRecordingRef = useRef(false);
  const recordPhaseRef = useRef("setup");

  const {
    mediaStreamRef,
    cameraReady, setCameraReady,
    cameraError,
    facingMode, videoInputCount, flipBusy,
    zoomCaps, zoom, torchSupported, torchOn, focusIndicator,
    startCamera, stopCameraStream,
    flipCamera, applyZoom, toggleTorch,
    handlePreviewTap, handleTouchStart, handleTouchMove, handleTouchEnd,
  } = useCameraStream({
    open,
    isRecordingRef,
    recordPhaseRef,
    liveVideoRef,
    stopRecordingRef,
  });

  const {
    recordPhase, setRecordPhase,
    isRecording, isPaused,
    recordedBlob, setRecordedBlob,
    recordedUrl, setRecordedUrl,
    recordTimer, setRecordTimer,
    recordStartedAt, setRecordStartedAt,
    recordTimerRef,
    startRecording, pauseRecording, resumeRecording, stopRecording,
    retakeRecording, discardRecording,
  } = useMediaRecorder({
    mediaStreamRef,
    liveVideoRef,
    getLocation: () => locationRef.current,
    onStop: ({ blob, durationSeconds }) => {
      setCameraReady(false);
      if (blob) setVideoDuration(selectDurationBracket(durationSeconds));
    },
    onRetake: async () => { await startCamera(); },
  });

  // Box recorder state/handlers into refs so useCameraStream — which was
  // initialised before useMediaRecorder — sees the latest values when its
  // event handlers fire. Plain state can't cross this init-order gap.
  useEffect(() => { stopRecordingRef.current = stopRecording; }, [stopRecording]);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  useEffect(() => { recordPhaseRef.current = recordPhase; }, [recordPhase]);

  // Re-attach the live stream to <video> whenever phase swaps back to
  // setup (e.g. after Retake). Lives in the parent — see comment in
  // useCameraStream about why the hook can't own this effect.
  useEffect(() => {
    if (recordPhase === "setup" && liveVideoRef.current && mediaStreamRef.current) {
      liveVideoRef.current.srcObject = mediaStreamRef.current;
    }
  }, [recordPhase, cameraReady, mediaStreamRef]);

  // Pre-fill form when a commission is attached (locked spec)
  useEffect(() => {
    if (!activeCommission) return;
    setVideoDuration(activeCommission.max_duration_seconds);
    setVideoStorage(activeCommission.storage_days);
    setVideoFeatured(!!activeCommission.featured);
    if (activeCommission.location) setLocation(activeCommission.location);
    setVideoTitle(activeCommission.title || "");
  }, [activeCommission]);

  const totalCost =
    BASE_COST +
    DURATION_COSTS[videoDuration] +
    STORAGE_COSTS[videoStorage] +
    (videoFeatured ? FEATURED_COST : 0);

  // ===== Location ref — kept in sync so the draft persistence path in
  // useMediaRecorder can grab the latest value via getLocation(). =====
  useEffect(() => { locationRef.current = location; }, [location]);
  // (Recording start/pause/resume/stop come from useMediaRecorder.)

  // ===== Drafts =====
  useEffect(() => {
    if (!open || draftBannerDismissed) return;
    (async () => {
      const draft = await loadDraft();
      if (!draft) return;
      const age = Date.now() - (draft.startedAt || 0);
      if (age >= DRAFT_EXPIRY_MS) return;
      setPendingDraft({
        startedAt: draft.startedAt,
        recordedSeconds: draft.recordedSeconds,
        blob: draft.blob,
        location: draft.location,
      });
    })();
  }, [open, draftBannerDismissed]);

  const resumeDraft = () => {
    if (!pendingDraft) return;
    const url = URL.createObjectURL(pendingDraft.blob);
    setRecordedBlob(pendingDraft.blob);
    setRecordedUrl(url);
    setRecordTimer(pendingDraft.recordedSeconds);
    recordTimerRef.current = pendingDraft.recordedSeconds;
    setRecordStartedAt(pendingDraft.startedAt);
    setVideoDuration(selectDurationBracket(pendingDraft.recordedSeconds));
    if (pendingDraft.location) setLocation(pendingDraft.location);
    setRecordPhase("review");
    setPendingDraft(null);
  };
  const dismissDraft = async () => {
    setPendingDraft(null);
    setDraftBannerDismissed(true);
    await clearDraft();
  };

  // ===== Reset on close =====
  const handleDialogChange = (nextOpen) => {
    if (!nextOpen) {
      setLocation(null);
      setLocationError("");
      if (recordedUrl) { try { URL.revokeObjectURL(recordedUrl); } catch (e) { /* ignore */ } }
      setRecordedBlob(null);
      setRecordedUrl(null);
      setRecordPhase("setup");
      setPendingDraft(null);
      setDraftBannerDismissed(false);
      stopCameraStream();
      // Release commission lock so other producers aren't blocked
      if (activeCommission && token) {
        api.post(`/commissions/${activeCommission.id}/unlock`, {}).catch(() => {});
        onCommissionDone?.();
      }
    }
    onOpenChange(nextOpen);
  };

  // ===== Publish — owned by useRecorderPublish. Handles both the
  // commission-submit and free-form upload paths, cache invalidation,
  // user refresh, and form/draft cleanup on success. =====
  const { creating, handlePublish, resetFormAfterPublish } = useRecorderPublish({
    user, token, refreshUser,
    videoTitle, videoDuration, videoStorage, videoFeatured,
    totalCost, location, recordedBlob, activeCommission,
    onVideoCreated, onCommissionDone, onOpenChange,
    setVideoTitle, setVideoDuration, setVideoStorage, setVideoFeatured, setLocation,
    discardRecording, stopCameraStream,
  });
  // (resetFormAfterPublish is returned so other call sites — e.g. a
  // future "save & exit" UI — can reuse the cleanup, but it's not
  // referenced inline below.)
  void resetFormAfterPublish;

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-[95vw] sm:max-w-lg mx-2 max-h-[90vh] overflow-y-auto">
        <DialogHeader className={isRecording ? "hidden" : ""}>
          <DialogTitle className="text-lg sm:text-xl">Create New Video</DialogTitle>
          <DialogDescription className="text-gray-400 text-xs sm:text-sm">
            Record live from your camera. Then choose duration, storage, and publish.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Focus mode — while actively recording, every non-essential
              block below is hidden so the creator sees a big viewfinder,
              the timer, and the controls. Nothing else. */}
          {!isRecording && <RecorderCommissionBanner commission={activeCommission} lockUntil={commissionLockUntil} />}

          {/* Location Status */}
          <div className={`bg-gray-800 rounded-lg p-3 ${isRecording ? "hidden" : ""}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-emerald-400" />
                <span className="text-sm text-gray-300">Location</span>
              </div>
              {locationLoading ? (
                <span className="text-xs text-gray-400">Detecting...</span>
              ) : location ? (
                <span className="text-xs text-emerald-400">{location.city}, {location.country}</span>
              ) : (
                <Button size="sm" variant="outline" onClick={getLocation} className="text-xs border-gray-700">
                  Enable Location
                </Button>
              )}
            </div>
            {locationError && (
              <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                <p className="text-amber-400 text-xs">{locationError}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={getLocation} className="text-xs border-gray-700" data-testid="retry-location-btn">
                    Retry
                  </Button>
                  <Button size="sm" variant="outline" onClick={useDefaultLocation} className="text-xs border-emerald-700 text-emerald-300 hover:bg-emerald-900/30" data-testid="use-default-location-btn">
                    Use default
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Pending draft banner */}
          {pendingDraft && recordPhase === "setup" && !isRecording && (
            <div className="bg-amber-900/30 border border-amber-500/40 rounded-lg p-3" data-testid="draft-banner">
              <p className="text-amber-200 text-xs">
                You have an unpublished recording from{" "}
                {new Date(pendingDraft.startedAt).toLocaleString()} ({pendingDraft.recordedSeconds}s).
                It will be auto-published after 12 hours if you don't act.
              </p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={resumeDraft} className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="resume-draft-btn">
                  Resume draft
                </Button>
                <Button size="sm" variant="outline" onClick={dismissDraft} className="border-gray-700 text-gray-300 text-xs" data-testid="dismiss-draft-btn">
                  Discard
                </Button>
              </div>
            </div>
          )}

          {/* Recorder */}
          <div className={`rounded-lg ${isRecording ? "bg-transparent p-0" : "bg-gray-800 p-3"}`} data-testid="video-recorder-block">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-emerald-400" />
                <span className="text-sm text-gray-300">
                  {recordPhase === "review"
                    ? `Recorded ${recordTimer}s — review & publish`
                    : isRecording
                      ? (isPaused ? "Paused" : "Recording…")
                      : cameraReady
                        ? "Camera live — tap Start to film"
                        : "Starting camera…"}
                </span>
              </div>
              {(isRecording || recordPhase === "review") && (
                <div className="flex items-center gap-1.5">
                  {isRecording && !isPaused && <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />}
                  {isPaused && <span className="w-2 h-2 rounded-full bg-amber-400" />}
                  <span className={`text-xs font-mono ${isPaused ? "text-amber-300" : "text-rose-300"}`} data-testid="record-timer">
                    {recordTimer}s / {MAX_RECORD_SECONDS}s
                  </span>
                </div>
              )}
            </div>

            <div className={`rounded-md overflow-hidden bg-black relative ${isRecording ? "h-[58vh] sm:h-[62vh]" : "aspect-video"}`}>
              {recordPhase === "review" ? (
                recordedUrl ? (
                  <>
                    {/* No native `controls` — iOS puts fullscreen/PiP
                        buttons on controlled videos; entering/exiting
                        that native fullscreen left Safari zoomed and
                        the dialog mangled (Eric, 2026-08-29). Tap
                        toggles play/pause instead. */}
                    <video
                      ref={reviewVideoRef}
                      src={recordedUrl}
                      autoPlay
                      loop
                      playsInline
                      disablePictureInPicture
                      onClick={(e) => {
                        const v = e.currentTarget;
                        if (v.paused) v.play(); else v.pause();
                      }}
                      className="w-full h-full object-contain cursor-pointer"
                      data-testid="review-video"
                    />
                    <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] text-white/80 bg-black/50 rounded-full px-2 py-0.5 pointer-events-none">
                      Tap to play / pause
                    </span>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                    Loading playback…
                  </div>
                )
              ) : (
                <>
                  <video
                    ref={liveVideoRef}
                    autoPlay
                    muted
                    playsInline
                    // Mirror the live preview ONLY when filming with the
                    // front camera — matches the user's mental model
                    // ("this is what I look like in a mirror"). The
                    // recorded file is never mirrored regardless;
                    // viewers always see the world the right way around.
                    className={`w-full h-full object-cover ${facingMode === "user" ? "scale-x-[-1]" : ""}`}
                    data-testid="live-preview-video"
                    onClick={handlePreviewTap}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  />
                  <RecorderCameraOverlays
                    focusIndicator={focusIndicator}
                    cameraReady={cameraReady}
                    isRecording={isRecording}
                    torchSupported={torchSupported}
                    torchOn={torchOn}
                    onToggleTorch={toggleTorch}
                    videoInputCount={videoInputCount}
                    facingMode={facingMode}
                    flipBusy={flipBusy}
                    onFlipCamera={flipCamera}
                    zoomCaps={zoomCaps}
                    zoom={zoom}
                    onApplyZoom={applyZoom}
                  />
                </>
              )}
            </div>

            {cameraError && <p className="text-amber-400 text-xs mt-2">{cameraError}</p>}

            <RecorderControlsBar
              recordPhase={recordPhase}
              isRecording={isRecording}
              isPaused={isPaused}
              cameraReady={cameraReady}
              onStart={startRecording}
              onPause={pauseRecording}
              onResume={resumeRecording}
              onStop={stopRecording}
              onRetake={retakeRecording}
            />

            <p className={`text-gray-500 text-[10px] mt-2 ${isRecording ? "hidden" : ""}`}>
              Drafts are kept for 12 hours. If you walk away, we'll auto-publish whatever you recorded and show you the bill.
            </p>
          </div>

          {recordPhase === "review" && (
            <>
              <RecorderPublishFormFields
                videoTitle={videoTitle}
                setVideoTitle={setVideoTitle}
                location={location}
                setLocation={setLocation}
                locationError={locationError}
                locationLoading={locationLoading}
                getLocation={getLocation}
                videoDuration={videoDuration}
                setVideoDuration={setVideoDuration}
                recordTimer={recordTimer}
                durationOptions={DURATION_OPTIONS}
                videoStorage={videoStorage}
                setVideoStorage={setVideoStorage}
                storageOptions={STORAGE_OPTIONS}
                videoFeatured={videoFeatured}
                setVideoFeatured={setVideoFeatured}
                user={user}
                commission={activeCommission}
              />

              <RecorderCostSummary
                commission={activeCommission}
                totalCost={totalCost}
                videoDuration={videoDuration}
                videoStorage={videoStorage}
                videoFeatured={videoFeatured}
                durationCosts={DURATION_COSTS}
                storageCosts={STORAGE_COSTS}
              />
            </>
          )}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={() => handleDialogChange(false)} className="text-gray-400 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={creating || !videoTitle || !recordedBlob}
            className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
            data-testid="submit-video-btn"
          >
            {creating ? "Uploading..." : `Publish (${totalCost.toFixed(2)} credits)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoRecorderDialog;
